import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';

import './UploadTicket.scss';
import { parseApiErrorMessage } from '../../utils/parseApiError';
import { Box, CircularProgress, Typography } from '@mui/material';
import { ManagerContext } from '../../App';

// Interface for the parsed ticket data from upload response
export interface CourtMatchCandidate {
  Court_id: number;
  Court_guid?: string;
  Court_Name: string;
  Court_County: string | null;
}

/**
 * What the server made of the court printed on the citation.
 *
 * 'unsure' is a normal, frequent answer and not an error: a wrong court is the address an LOR is
 * filed to, so the server hands back candidates rather than picking one. Render the reason.
 */
export interface CourtMatch {
  status: 'matched' | 'unsure' | 'none';
  courtId: number | null;
  courtGuid: string | null;
  candidates: CourtMatchCandidate[];
  reason: string;
}

export interface ParsedTicketData {
  Ticket_Number?: string;
  Ticket_Citation?: string;
  Ticket_Address?: string;
  Ticket_City?: string;
  Ticket_County?: string;
  Ticket_CourtDate?: string;
  /** The court as printed. Matching it is the server's job and may come back unsure. */
  Ticket_CourtName?: string;
  /** The respond-by deadline. A REQUIRED form field the scan never used to fill. */
  Ticket_RespondBy?: string;
  // Read by the parser since Aug 24 and dropped by the upload endpoint ever since, which is why
  // they are still empty on every case and preservation demands still go out over ruled blanks.
  Ticket_ArrestingAgency?: string;
  Ticket_AgencyCaseNumber?: string;
  Ticket_LicensePlate?: string;
  Ticket_LicenseType?: string;
  courtMatch?: CourtMatch | null;
}

const UploadTicket = (props: {
  uploaded(
    documentId: string,
    parsedData?: ParsedTicketData,
    meta?: { ocrSucceeded: boolean },
  ): void;
  User_guid?: string;
}) => {
  const manager = useContext(ManagerContext);

  // Check if User_guid is provided but manager is not logged in
  React.useEffect(() => {
    if (props.User_guid && !manager) {
      throw new Error(
        'User_guid prop can only be used when a manager is logged in',
      );
    }
  }, [props.User_guid, manager]);

  const [loaded, setLoaded] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string>('');
  const [parseFailed, setParseFailed] = React.useState(false);
  const [isDragOver, setIsDragOver] = React.useState(false);

  const processFile = async (file: File) => {
    if (!file) {
      return;
    }

    const isImage = file.type.startsWith('image/');
    const isPdf =
      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

    if (!isImage && !isPdf) {
      setUploadError('Please select an image or PDF file');
      return;
    }

    setLoading(true);
    setUploadError('');
    setParseFailed(false);

    try {
      // Create FormData to send the file
      const formData = new FormData();
      formData.append('image', file);

      // Add User_guid to the form data if provided (for manager uploads)
      if (props.User_guid) {
        formData.append('User_guid', props.User_guid);
      }

      // Upload to the /ticket-upload route
      const response = await fetch(
        process.env.API_HOST + '/api/v1/ticket-upload',
        {
          method: 'POST',
          credentials: 'include', // Include cookies for authentication
          body: formData,
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          parseApiErrorMessage(errorData, `Upload failed (${response.status})`),
        );
      }

      const result = await response.json();
      console.log('Upload successful:', result);

      const ocrSucceeded = !result.parseError && result.parsed !== false;
      if (!ocrSucceeded) {
        setParseFailed(true);
      }

      setLoaded(true);
      props.uploaded(
        result.document_guid || '',
        {
          Ticket_Number: result.Ticket_Number,
          Ticket_Citation: result.Ticket_Citation,
          Ticket_Address: result.Ticket_Address,
          Ticket_City: result.Ticket_City,
          Ticket_County: result.Ticket_County,
          Ticket_CourtDate: result.Ticket_CourtDate,
          Ticket_LicenseType: result.Ticket_LicenseType,
        },
        { ocrSucceeded },
      );
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'Upload failed');
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const uploadTicket = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (loading === true) {
      return;
    }

    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;

    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      processFile(files[0]);
    }
  };

  return (
    <div
      className={`upltick ${isDragOver ? 'drag-over' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <input
        disabled={loading}
        onChange={uploadTicket}
        className="upltick-in"
        type="file"
        accept="image/*,application/pdf,.pdf"
      />
      {/*
        Lloyd, Aug 29 2026, after starting a case himself for the first time: *"when it came time to
        upload the citation, I had to save it to my computer and then open it, as opposed to a
        simple drag and drop option. Can we get a drag and drop option?"*

        The drop handler above has been live since June. The problem was that nothing on the screen
        said so — the only cue was the overlay below, which appears once you are ALREADY dragging a
        file over the box, so a person who does not think to try never finds out it is there. A
        feature nobody can discover is not a feature. Hence a standing hint, not a new handler.
      */}
      {!loading && !loaded && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Or drag and drop the citation straight onto the box above — photo or PDF.
        </Typography>
      )}
      {isDragOver && (
        <div className="drag-overlay">
          <Typography variant="h6">Drop your file here</Typography>
        </div>
      )}
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          <CircularProgress size={24} />
          <Typography>
            Reading your ticket... this may take a moment.
          </Typography>
        </Box>
      )}
      {uploadError && <Typography color="error">{uploadError}</Typography>}
      {loaded && !uploadError && !parseFailed && (
        <Typography color="success">
          File uploaded and parsed successfully!
        </Typography>
      )}
      {loaded && !uploadError && parseFailed && (
        <>
          <Typography color="success">File uploaded successfully!</Typography>
          <Typography color="text.secondary">
            We couldn&apos;t read ticket details from this file automatically.
            Please review and enter them on the next screen.
          </Typography>
        </>
      )}
      {loaded && uploadError && (
        <>
          <Typography>
            Upload failed, but you can still enter your ticket details manually.
          </Typography>
        </>
      )}
    </div>
  );
};
export default UploadTicket;
